import { Signal, inject, DestroyRef, effect } from '@angular/core';

import { EscapeManager } from 'src/app/core/services/escape-manager.service';

import { SuiDialogRef } from '../sui-dialog-ref';
import { DialogCloseOptions } from '../dialog-result';

import { EscapePriority } from 'src/app/core/constants';

/**
 * Dirty-state protection for dialogs.
 * Prevents accidental closing and runs async confirmation logic when needed.
 *
 * @template TData   Data passed TO the dialog.
 * @template TResult Data returned FROM the dialog.
 */
export function injectDirtyCloseHandler<TData = any, TResult = any>(
  dialogRef?: SuiDialogRef<TData, TResult>,
) {
  const ref = dialogRef ?? inject(SuiDialogRef<TData, TResult>);
  const destroyRef = inject(DestroyRef);
  const escapeManager = inject(EscapeManager);

  let beforeCloseAttempt: (() => Promise<boolean>) | undefined;
  let dirty: Signal<boolean> | undefined;

  // * Disable auto-close while the dialog is dirty
  effect(() => {
    if (dirty) {
      ref.ngpRef.disableClose = dirty();
    }
  });

  // * Enables dirty-close protection.
  // * Registers async confirmation logic.
  // * confirmClose -> async function -> returns [true - close is allowed], [false - close is prevented]
  function protectClosingWhenDirty(
    dirtyState: Signal<boolean>,
    confirmClose: () => Promise<boolean>,
  ) {
    beforeCloseAttempt = confirmClose;
    dirty = dirtyState;

    let isConfirming = false;

    // * Register with EscapeManager for dirty state protection
    const escapeHandlerId = escapeManager.register(() => {
      if (!dirtyState()) {
        return false; // ! not dirty, let default dialog close handle it
      }

      // ! Let ESC reach the confirmation dialog naturally so it can close itself
      if (isConfirming) {
        return false; // ! let confirm dialog handle escape
      }

      // * Block ESC while dirty and show confirmation
      isConfirming = true;

      confirmClose()
        .then((allowed) => {
          if (allowed) {
            // * Allow close and let ng-primitives emit proper focusOrigin
            ref.ngpRef.disableClose = false;
            ref.ngpRef.close();
          }
        })
        .finally(() => {
          isConfirming = false;
        });

      return true; // ! handled - stop propagation
    }, EscapePriority.DIALOG_GUARD);

    destroyRef.onDestroy(() => {
      escapeManager.unregister(escapeHandlerId);
    });
  }

  /**
   * * Attempts to close the dialog.
   * * Runs dirty confirmation first.
   *
   * @returns true if closing was allowed, false if prevented.
   */
  async function tryClose(
    options: DialogCloseOptions<TResult>,
  ): Promise<boolean> {
    if (beforeCloseAttempt && !(await beforeCloseAttempt())) {
      return false;
    }

    ref.ngpRef.disableClose = false;
    ref.close(options);
    return true;
  }

  /**
   * * Closes immediately.
   * * Skips all dirty-state checks.
   * Use after confirmed actions (e.g. successful save).
   */
  function closeImmediately(options: DialogCloseOptions<TResult>): void {
    ref.ngpRef.disableClose = false;
    ref.close(options);
  }

  // TODO:
  // ! Backdrop click protection not implemented.
  // * If needed, treat backdrop clicks like ESC when dirty.
  // * For now: use `closeOnClick: false` when dirty protection is required.

  return {
    tryClose,
    closeImmediately,
    protectClosingWhenDirty,
  };
}
